import { Loading } from "types/loadingType";

import {
    Button,
    Classes,
    Icon,
    Spinner,
    SpinnerSize,
    Tooltip
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
    CellClickedEvent,
    GetRowIdParams,
    ICellRendererParams
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import classNames from "classnames";
import { segmentTrackExportClusterProjects } from "in_queue/analytics";
import { FavoriteProjectStar } from "in_queue/components/FavoriteProjectStar";
import { ClusterProjectsBaseScenario } from "in_queue/hooks/useClusterProjectRows";
import {
    ClusterProjectRow,
    getClusterProjectDisplayName,
    getStudyGroupDisplayLabel
} from "in_queue/types/clusterProjectRowType";
import { getClusterLabel, LegacyClusterInfo } from "in_queue/types/clusterType";
import { getRegionConfig } from "in_queue/types/configPerRegion";
import { RefObject, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RegionType } from "types/regionType";
import { AgGridColumnDefs } from "./agGrid/agGridHelpers";
import {
    baseScenarioColumnGroup,
    BaseScenarioColumnId
} from "./agGrid/baseScenarioColumns";
import { BLUEPRINT_SORT_ICONS } from "./agGrid/sortIcons";
import { BaseAgGrid } from "./base/BaseAgGrid";
import css from "./ClusterProjectsTable.module.scss";
import { FuelTypePillRenderer } from "./FuelTypePill";
import tableCss from "./TableStyles.module.scss";

type ColumnId =
    | "projectId"
    | "tracked"
    | "studyGroup"
    | "fuelType"
    | "energySize"
    | "capacitySize"
    | BaseScenarioColumnId;

const DEFAULT_COL_DEF = {
    resizable: true,
    unSortIcon: true,
    useValueFormatterForExport: false
};

const PROJECT_ROW_HEIGHT = 55;
const HEADER_ROW_HEIGHT = 42;
const GROUP_HEADER_ROW_HEIGHT = 26;
// Every column flexes. The sticky header forces `overflow-x: clip` on the
// grid, so a column set wider than the page is silently cut off instead of
// scrolling. These sum to 896px, so the full set fits a 1000px viewport.
//
// Every numeric column is sized by its header, not its values: the headers are
// uppercase and letter-spaced, so `TOTAL COST` is wider than the costs beneath
// it and `RANK` is wider than its `P94` pill. The size columns take the widest
// label any region uses (`CAPACITY`) rather than switching on the region.
const PROJECT_MIN_WIDTH = 150;
// "GROUP" rather than "STUDY GROUP" as the header: the column only appears on
// clusters that span groups, and the longer label costs 40px the other columns
// would have to give up.
const GROUP_MIN_WIDTH = 90;
const FUEL_TYPE_MIN_WIDTH = 120;
// The "COMBUSTION TURBINE" pill measures ~194px, and the cell adds ~24px of
// padding. The two longer values in the data ("Combustion turbine/storage",
// "Natural gas; solar; other") ellipsize.
const FUEL_TYPE_MAX_WIDTH = 225;
const SIZE_MIN_WIDTH = 112;
// `height="auto"` renders every row in the DOM, so bound it. Each row mounts
// a link plus three Blueprint popovers (name, lock, rank pill).
const PAGE_SIZE = 50;

// Applied to the header cells that span both header rows so they clear the
// group-row padding below and centre across the full header height.
const VCENTER_HEADER_CLASS = "ClusterProjectsTable-vcenter-header";

const SIZE_GROUP_NAME = "Size (MW)";
const LOCKED_TOOLTIP = "Contact your account manager to unlock this project";
// Blueprint defaults to "auto", which lands the lock tooltip to the right of
// the icon, on top of the project name.
const TOOLTIP_PLACEMENT = "bottom-start";

const NoProjectsOverlay: React.FC = () => (
    <span className={tableCss["muted-cell"]}>No projects to show</span>
);

// Stands in for the whole grid while the rows load. AG Grid's own overlay
// centres itself in an empty body, which under `autoHeight` is a few pixels
// tall and lands on top of the column headers.
const LoadingProjects: React.FC = () => (
    <div className={css["ClusterProjectsTable-loading"]}>
        <Spinner size={SpinnerSize.STANDARD} />
    </div>
);

const TrackedStatusIcon: React.FC<{ isTracked: boolean }> = ({ isTracked }) => {
    const icon = (
        <Icon
            className={classNames(css["ClusterProjectsTable-tracked-status"], {
                [css["ClusterProjectsTable-tracked-status--locked"]]: !isTracked
            })}
            icon={isTracked ? IconNames.UNLOCK : IconNames.LOCK}
        />
    );
    return isTracked ? (
        icon
    ) : (
        <Tooltip content={LOCKED_TOOLTIP} placement={TOOLTIP_PLACEMENT} compact>
            {icon}
        </Tooltip>
    );
};

// Haven't been able to find any good typings for cell renderers
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ProjectCellRenderer: any = (
    params: ICellRendererParams<ClusterProjectRow>
) => {
    const project = params.data;
    if (!project) {
        return null;
    }
    const displayName = getClusterProjectDisplayName(project);
    return (
        <div className={css["ClusterProjectsTable-project-cell"]}>
            <FavoriteProjectStar
                projectId={project.projectId}
                isFavorited={project.isFavorited}
            />
            <TrackedStatusIcon isTracked={project.isTracked} />
            <Tooltip
                content={displayName}
                placement={TOOLTIP_PLACEMENT}
                compact
                className={css["ClusterProjectsTable-project-tooltip"]}
            >
                {project.isTracked ? (
                    <Link
                        to={project.projectId}
                        className={classNames(
                            css["ClusterProjectsTable-project"],
                            Classes.TEXT_LARGE
                        )}
                    >
                        {displayName}
                    </Link>
                ) : (
                    <span
                        className={classNames(
                            css["ClusterProjectsTable-project-locked"],
                            Classes.TEXT_LARGE
                        )}
                    >
                        {displayName}
                    </span>
                )}
            </Tooltip>
        </div>
    );
};

const getColumnDefs = (
    region: RegionType,
    baseScenario: ClusterProjectsBaseScenario | undefined,
    showStudyGroup: boolean
): AgGridColumnDefs<ClusterProjectRow, ColumnId> => {
    const config = getRegionConfig(region);

    const baseHeaderTooltip = baseScenario
        ? `Latest base scenario: "${baseScenario.title}"${
              baseScenario.runCompletedAt
                  ? ` · ran ${baseScenario.runCompletedAt.toLocaleDateString()}`
                  : ""
          }`
        : "Latest succeeded SHARED (base) scenario for this cluster — no data yet";

    return [
        {
            colId: "projectId",
            headerName: "Project",
            headerClass: VCENTER_HEADER_CLASS,
            flex: 1.7,
            minWidth: PROJECT_MIN_WIDTH,
            valueGetter: ({ data }) =>
                data ? getClusterProjectDisplayName(data) : null,
            cellRenderer: ProjectCellRenderer
        },
        {
            colId: "tracked",
            headerName: "Tracked",
            hide: true,
            valueGetter: ({ data }) =>
                data === undefined ? null : data.isTracked ? "Yes" : "No"
        },
        ...(showStudyGroup
            ? [
                  {
                      colId: "studyGroup" as const,
                      headerName: "Group",
                      headerTooltip:
                          "The project's study group within this cluster.",
                      headerClass: VCENTER_HEADER_CLASS,
                      flex: 0.6,
                      minWidth: GROUP_MIN_WIDTH,
                      valueGetter: ({
                          data
                      }: {
                          data: ClusterProjectRow | undefined;
                      }) => getStudyGroupDisplayLabel(region, data?.studyGroup)
                  }
              ]
            : []),
        {
            colId: "fuelType",
            field: "fuelType",
            headerName: "Fuel Type",
            headerClass: VCENTER_HEADER_CLASS,
            flex: 1.2,
            minWidth: FUEL_TYPE_MIN_WIDTH,
            maxWidth: FUEL_TYPE_MAX_WIDTH,
            cellRenderer: FuelTypePillRenderer
        },
        {
            headerName: SIZE_GROUP_NAME,
            suppressStickyLabel: true,
            children: [
                {
                    colId: "energySize",
                    headerName: config.sizeLabel.energyLabel,
                    type: "numericColumn",
                    flex: 0.6,
                    minWidth: SIZE_MIN_WIDTH,
                    valueGetter: ({ data }) => data?.size.erisMw ?? null
                },
                {
                    colId: "capacitySize",
                    headerName: config.sizeLabel.capacityLabel,
                    type: "numericColumn",
                    flex: 0.6,
                    minWidth: SIZE_MIN_WIDTH,
                    valueGetter: ({ data }) => data?.size.nrisMw ?? null
                }
            ]
        },
        baseScenarioColumnGroup<ClusterProjectRow>({
            getStats: (data) => data,
            headerTooltip: baseHeaderTooltip,
            includePercentile: true
        })
    ];
};

const getProjectRowId = ({ data }: GetRowIdParams<ClusterProjectRow>) =>
    data.projectId;

const isButtonClick = (event: CellClickedEvent<ClusterProjectRow>): boolean =>
    event.event?.target instanceof Element &&
    event.event.target.closest("button") !== null;

export type ClusterProjectsGridRef = RefObject<AgGridReact<ClusterProjectRow>>;

export const ClusterProjectsExportButton: React.FC<{
    gridRef: ClusterProjectsGridRef;
    cluster: LegacyClusterInfo;
    disabled: boolean;
}> = ({ gridRef, cluster, disabled }) => {
    const onClickExport = () => {
        const api = gridRef.current?.api;
        if (api === undefined) {
            return;
        }
        api.exportDataAsCsv({
            fileName: `Nira Export - ${getClusterLabel(
                cluster
            )} - Projects.csv`,
            skipColumnGroupHeaders: true,
            // Carries the hidden Percentile column into the file.
            allColumns: true
        });
        segmentTrackExportClusterProjects({
            cluster,
            numRows: api.getDisplayedRowCount()
        });
    };

    return (
        <Button
            className={css["ClusterProjectsTable-export-button"]}
            text="Export"
            icon={IconNames.EXPORT}
            variant="minimal"
            disabled={disabled}
            onClick={onClickExport}
        />
    );
};

export const ClusterProjectsTable: React.FC<{
    cluster: LegacyClusterInfo;
    projects: Loading<ClusterProjectRow[]>;
    baseScenario: ClusterProjectsBaseScenario | undefined;
    showStudyGroup: boolean;
    rowFilter?: (row: ClusterProjectRow) => boolean;
    gridRef?: ClusterProjectsGridRef;
    quickFilterText?: string;
    stickyHeader?: boolean;
}> = ({
    cluster,
    projects,
    baseScenario,
    showStudyGroup,
    rowFilter,
    gridRef,
    quickFilterText,
    stickyHeader
}) => {
    const navigate = useNavigate();

    // A fresh array identity makes AG Grid rebuild every column, which the
    // search box would otherwise trigger on each keystroke.
    const columnDefs = useMemo(
        () => getColumnDefs(cluster.region, baseScenario, showStudyGroup),
        [cluster.region, baseScenario, showStudyGroup]
    );

    if (projects === "loading") {
        return <LoadingProjects />;
    }

    return (
        <BaseAgGrid<ClusterProjectRow>
            ref={gridRef}
            className={css["ClusterProjectsTable"]}
            loadingData={projects}
            columnDefs={columnDefs}
            defaultColDef={DEFAULT_COL_DEF}
            icons={BLUEPRINT_SORT_ICONS}
            height={"auto"}
            stickyHeader={stickyHeader}
            rowHeight={PROJECT_ROW_HEIGHT}
            headerHeight={HEADER_ROW_HEIGHT}
            groupHeaderHeight={GROUP_HEADER_ROW_HEIGHT}
            analyticsTableName="Cluster Projects"
            rowFilter={rowFilter}
            // Row ids let updates flow as transactions. Favoriting changes a
            // default sort key, so keep rows where they are instead of
            // following the re-sorted array.
            getRowId={getProjectRowId}
            suppressMaintainUnsortedOrder
            pagination
            paginationPageSize={PAGE_SIZE}
            paginationPageSizeSelector={false}
            quickFilterText={quickFilterText}
            noRowsOverlayComponent={NoProjectsOverlay}
            onCellClicked={(event) => {
                // Locked projects have no page to open.
                if (!isButtonClick(event) && event.data?.isTracked) {
                    navigate(event.data.projectId);
                }
            }}
            getRowStyle={({ data }) =>
                data?.isTracked ? undefined : { cursor: "default" }
            }
            selectableRows
        />
    );
};
